import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatTimeAgo } from '@bookingdei/i18n/format-date';
import { api } from '../api';
import { useLanguage } from './language-context';
import { useResource } from './use-resource';
import { useLayer, LayerScrim } from './layer-stack';
import type { NotificationItem, NotificationList } from '../mock/seeds/schema-types';
import './notification-tray.css';

// Notification tray (FR-C28, DR-38; CB-17 `notification-tray.tsx`) — replaces the
// retired /notifications page. The design derives its tray on the client from booking
// states (Others.dc.html L1418-1450); FR-C28 requires the REAL feed instead: it reads
// GET /v1/me/notifications (the same records that drive push/SMS) and marks read via
// POST /v1/me/notifications/read — one item on open, or all with "Mark all read".
// Each item deep-links to its target through `entity_ref`. The popover sits on the
// layer stack (Esc closes it, focus returns to the bell) without a history entry, so
// following an item's link is a plain navigation.

export interface NotificationFeed {
  items: NotificationItem[];
  unread: number;
  error: unknown;
  loading: boolean;
  reload: () => void;
  markRead: (ids?: string[]) => Promise<void>;
}

/** The customer's notification feed + read actions; re-checks when `refreshKey` changes. */
export function useNotificationFeed(refreshKey: unknown): NotificationFeed {
  const { data, error, loading, reload } = useResource<NotificationList>(
    (signal) => api.GET('/me/notifications', { signal }),
    [refreshKey],
  );
  const items = data?.items ?? [];
  const unread = items.filter((n) => !n.read_at).length;

  const markRead = useCallback(
    async (ids?: string[]) => {
      try {
        await api.POST('/me/notifications/read', { body: ids ? { ids } : {} });
      } finally {
        reload();
      }
    },
    [reload],
  );

  return { items, unread, error, loading, reload, markRead };
}

/** In-app target of a notification's `entity_ref` ('booking:ID', 'review:ID', …), if any. */
export function notificationTarget(entityRef: string | undefined): string | null {
  if (!entityRef) return null;
  const i = entityRef.indexOf(':');
  const kind = i === -1 ? entityRef : entityRef.slice(0, i);
  const id = i === -1 ? '' : entityRef.slice(i + 1);
  if (kind === 'booking' && id) return `/bookings?b=${encodeURIComponent(id)}`;
  if (kind === 'review') return '/reviews';
  if (kind === 'thread' || kind === 'message') return '/messages';
  if (kind === 'claim') return '/claims';
  // Unknown kinds are tolerated, not guessed at (AP-D10).
  return null;
}

type Tone = 'ok' | 'warn' | 'info' | 'danger' | 'accent' | 'neutral';

// Dot colour per notification kind, on the design's tone scale (bd-core.js TONE).
function toneOf(kind: string): Tone {
  if (/reject|cancel|expire|no_show|fail/.test(kind)) return 'danger';
  if (/confirm|verified|complete|refund/.test(kind)) return 'ok';
  if (/pending|waiting|remind/.test(kind)) return 'warn';
  if (/review/.test(kind)) return 'accent';
  if (/proof|payment|booking/.test(kind)) return 'info';
  return 'neutral';
}

export function NotificationTray({
  open,
  onClose,
  feed,
}: {
  open: boolean;
  onClose: () => void;
  feed: NotificationFeed;
}) {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const { z } = useLayer(open, onClose, { kind: 'sheet', history: false });
  const panelRef = useRef<HTMLDivElement>(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!open) return;
    setNow(new Date());
    feed.reload();
    panelRef.current?.focus();
    // Refresh on open only; `feed.reload` is stable per mount.
  }, [open]);

  if (!open) return null;

  const openItem = (n: NotificationItem) => {
    if (!n.read_at) void feed.markRead([n.id]);
    const target = notificationTarget(n.entity_ref);
    onClose();
    if (target) navigate(target);
  };

  return (
    <LayerScrim z={z} onClose={onClose} className="popover-catcher">
      <div
        ref={panelRef}
        className="notif-tray"
        role="dialog"
        aria-label={t('user.shell.notif.title')}
        tabIndex={-1}
        data-motion=""
      >
        <div className="notif-tray__head">
          <span className="notif-tray__title">{t('user.shell.notif.title')}</span>
          {feed.unread > 0 && (
            <button
              type="button"
              className="notif-tray__mark"
              onClick={() => {
                void feed.markRead();
                onClose();
              }}
            >
              {t('user.shell.notif.mark_all')}
            </button>
          )}
        </div>
        <div className="notif-tray__list">
          {feed.error && feed.items.length === 0 ? (
            <div className="notif-tray__empty">
              <span className="notif-tray__empty-sub">{t('user.shell.notif.error')}</span>
              <button type="button" className="notif-tray__mark" onClick={feed.reload}>
                {t('action.retry')}
              </button>
            </div>
          ) : feed.items.length === 0 ? (
            !feed.loading && (
              <div className="notif-tray__empty">
                <span className="notif-tray__empty-title">{t('user.shell.notif.empty_title')}</span>
                <span className="notif-tray__empty-sub">{t('user.shell.notif.empty_sub')}</span>
              </div>
            )
          ) : (
            feed.items.map((n) => {
              const body = lang === 'bn' && n.body_bn ? n.body_bn : n.body_en;
              return (
                <button
                  key={n.id}
                  type="button"
                  className={n.read_at ? 'notif-tray__item' : 'notif-tray__item is-unread'}
                  onClick={() => openItem(n)}
                >
                  <span className="notif-tray__dot" data-tone={toneOf(n.kind)} aria-hidden="true" />
                  <span className="notif-tray__text">{body}</span>
                  <span className="notif-tray__when">{formatTimeAgo(lang, new Date(n.created_at), now)}</span>
                </button>
              );
            })
          )}
        </div>
      </div>
    </LayerScrim>
  );
}
